import { useVehicleEditing } from 'context/isAnotherUserEditingContext';
import { useSaveGrade } from 'pages/ListerVehicleUploadV2/hooks/useSaveGrade';
import { useCallback, useMemo } from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import styled from 'styled-components';

type Props = {
  sectionId: string;
  onSelect?: (grade: string) => void;
  background?: string;
};

const GradeRow = styled.div`
  display: flex;
  gap: 6.75px;
`;

const GradeButton = styled.div<{
  selectedColor: string;
  isSelected?: boolean;
  background?: string;
  disabled?: boolean;
}>`
  border: 1px solid #999999;
  border-radius: 8px;
  background: ${props => (props.isSelected ? props.selectedColor : props.background || '#fff')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  user-select: none;
  flex: 1 1 100%;
  font-weight: ${props => (props.isSelected ? 600 : 400)};
  line-height: 16px;
  color: ${props => (props.isSelected ? '#fff' : '#000')};
  height: 40px;
  text-transform: capitalize;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
`;

const gradeColorMap: Record<string, string> = {
  poor: 'rgba(175, 41, 48, 1)',
  fair: 'rgba(243, 175, 103, 1)',
  good: 'rgba(71, 151, 71, 1)'
};

const possibleGrades = ['poor', 'fair', 'good'];

export const GradeField = (props: Props) => {
  return (
    <Field name={`${props.sectionId}.grade`}>
      {fieldProps => (
        <GradeRow>
          {possibleGrades.map(grade => (
            <GradeFiledComponent {...props} {...fieldProps} grade={grade} />
          ))}
        </GradeRow>
      )}
    </Field>
  );
};

const GradeFiledComponent = ({
  background,
  input,
  onSelect,
  sectionId,
  grade
}: FieldRenderProps<any> & Props & { grade: string }) => {
  const save = useSaveGrade(sectionId);

  const isSelected = input.value === grade;

  const { hasAnotherEditor } = useVehicleEditing();

  const selectGrade = useCallback(() => {
    if (onSelect) onSelect(grade);
    input.onChange(grade);
    save(grade);
  }, [input.onChange, save, onSelect, grade]);

  const deselectGrade = useCallback(() => {
    if (!isSelected) return;
    if (onSelect) onSelect('');
    input.onChange('');
    save('');
  }, [input.onChange, isSelected, onSelect, save]);

  const handleClick = useCallback(() => {
    if (isSelected) deselectGrade();
    else selectGrade();
  }, [selectGrade, deselectGrade, isSelected]);

  return (
    <GradeButton
      background={background}
      selectedColor={gradeColorMap[grade]}
      isSelected={input.value === grade}
      disabled={hasAnotherEditor}
      onClick={handleClick}
    >
      {grade}
    </GradeButton>
  );
};
